import type {
  BusFilterType,
  GroupResponsibleState,
} from '@/scopes/account/group-responsible/domain/types'
import { sortParticipants } from './utils'

const DAYS_BEFORE = 10

export function getParticipantsWithBusInfo(state: GroupResponsibleState) {
  const { participants, buses } = state
  if (!participants)
    return []

  return Object.entries(participants).map(([participantId, participant]) => {
    const associatedBuses = Object.values(buses).filter(bus => bus.participantsId.includes(Number(participantId)))

    const outboundBus = associatedBuses.find(bus => bus.direction === 'Outbound')
    const inboundBus = associatedBuses.find(bus => bus.direction === 'Inbound')
    const hasLocation = outboundBus?.departureLocation || inboundBus?.arrivalLocation
    const location = hasLocation
      ? [outboundBus?.departureLocation, inboundBus?.arrivalLocation].filter(Boolean).join(' - ')
      : '-'
    const hasNumber = outboundBus?.busNumber || inboundBus?.busNumber
    const busNumber = hasNumber ? [outboundBus?.busNumber, inboundBus?.busNumber].filter(Boolean).join(' - ') : '-'

    const label = (() => {
      if (associatedBuses.length) {
        return {
          state: 'success',
          text: 'groups_responsible_status_booked',
        }
      }

      if (participant.assigned) {
        return {
          state: 'info',
          text: 'groups_responsible_status_not_applicable',
        }
      }

      return {
        state: 'error',
        text: 'groups_responsible_status_not_booked',
      }
    })()

    const participantData = {
      id: participantId,
      name: participant.name,
      email: participant.email,
      assigned: participant.assigned,
      isBusAssignmentProcessing: participant.isBusAssignmentProcessing,
      busInfo: associatedBuses.length
        ? {
            number: busNumber,
            location,
          }
        : null,
      label,
    }

    return participantData
  })
}

export function getParticipantsBusBookedCounts(state: GroupResponsibleState): {
  all: number
  notBooked: number
  booked: number
} {
  const { participants, buses } = state
  if (!participants)
    return { all: 0, notBooked: 0, booked: 0 }

  const all = Object.keys(participants).length
  const notAssigned = Object.values(participants).filter(p => !p.assigned).length

  const booked = Object.keys(participants).filter(participantId =>
    Object.values(buses).some(bus => bus.participantsId.includes(Number(participantId))),
  ).length

  return { all, notBooked: notAssigned, booked }
}

export function getFilteredParticipantsBusBooked(state: GroupResponsibleState, filter: BusFilterType, searchTerm = '') {
  const participantsWithBusInfo = getParticipantsWithBusInfo(state)
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  const filteredParticipants = participantsWithBusInfo.filter((participant) => {
    const matchesFilter = filter === 'all' || participant.label.text === `groups_responsible_status_${filter}`

    const matchesSearch = trimmedSearchTerm === '' || participant.name.toLowerCase().includes(trimmedSearchTerm)

    return matchesFilter && matchesSearch
  })

  return filteredParticipants.sort(sortParticipants)
}

export function getBusAssignmentDeadLine(state: GroupResponsibleState, locale: string): {
  deadLineDate: string
  daysBefore: number
  isOverDeadline: boolean
} {
  const defaultValue = {
    deadLineDate: '',
    daysBefore: DAYS_BEFORE,
    isOverDeadline: true,
  }
  const startingDate = state.bookingData?.group?.startingDate
  if (!startingDate) {
    return defaultValue
  }

  const date = new Date(startingDate)
  if (Number.isNaN(date.getTime())) {
    return defaultValue
  }

  const limitDate = new Date(date)
  limitDate.setDate(date.getDate() - DAYS_BEFORE)
  limitDate.setHours(23, 59, 59, 999)

  const deadLineDate = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(limitDate)

  const now = new Date()
  const isOverDeadline = now > limitDate

  return { deadLineDate, daysBefore: DAYS_BEFORE, isOverDeadline }
}

export function getParticipantsEditingData(state: GroupResponsibleState) {
  const selectedParticipantIds = state.selectedParticipantIds
  const participants = state.participants || {}
  const selectedCount = selectedParticipantIds.size
  const totalCount = Object.keys(participants).length

  return {
    selectedCount,
    totalCount,
  }
}

export function getSelectedParticipantsStatus(state: GroupResponsibleState): {
  hasProcessingParticipants: boolean
  hasNonBusTransportParticipants: boolean
} {
  const { selectedParticipantIds, participants } = state

  if (!participants || selectedParticipantIds.size === 0) {
    return {
      hasProcessingParticipants: false,
      hasNonBusTransportParticipants: false,
    }
  }

  const selectedParticipants = Array.from(selectedParticipantIds)
    .map(id => participants[id])
    .filter(Boolean)

  const hasProcessingParticipants = selectedParticipants.some(
    participant => participant.isBusAssignmentProcessing,
  )
  const hasNonBusTransportParticipants = selectedParticipants.some(
    participant => participant.transportType && participant.transportType !== 'bus',
  )

  return {
    hasProcessingParticipants,
    hasNonBusTransportParticipants,
  }
}
